// Modale « Redémarrer » de la fiche appareil : redémarrage immédiat ou
// planifié SANS installer de mises à jour, et liste des commandes planifiées
// de l'appareil (redémarrages, mises à jour) avec annulation.
//
// Le redémarrage immédiat exige une phrase de confirmation : c'est la machine
// d'un client, et un clic de trop la coupe en pleine journée. La planification
// propose par défaut ce soir 22 h locale, même raisonnement que
// SystemUpdatesPanel.

import { useState } from 'react';
import { X, Power, AlertTriangle, Clock } from 'lucide-react';
import { api } from '../lib/api.jsx';

const CONFIRM_PHRASE = 'REDÉMARRER';

const TYPE_LABELS = {
  1: 'Redémarrage',
  2: 'Arrêt',
  3: 'Mises à jour',
  6: 'Mises à jour',
  20: 'Mise à jour RouterOS',
  21: 'Sauvegarde',
};
export const scheduledLabel = (c) => TYPE_LABELS[c.type] || 'Commande';

// Format attendu par <input type="datetime-local"> : heure LOCALE, sans fuseau.
const toLocalInputValue = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const defaultTonight = () => {
  const d = new Date();
  d.setHours(22, 0, 0, 0);
  if (d <= new Date()) d.setDate(d.getDate() + 1);
  return toLocalInputValue(d);
};

const fmtWhen = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

export function RestartModal({ agentId, label, isServer, scheduled, canRun, onClose }) {
  const [mode, setMode] = useState('schedule'); // 'now' | 'schedule'
  const [whenLocal, setWhenLocal] = useState(defaultTonight);
  const [reason, setReason] = useState('');
  const [maintenance, setMaintenance] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [changed, setChanged] = useState(false);
  const [cancelled, setCancelled] = useState([]);

  const whenInvalid = mode === 'schedule' && (!whenLocal || new Date(whenLocal) <= new Date());
  const confirmOk = mode !== 'now' || confirmText.trim().toUpperCase() === CONFIRM_PHRASE;
  const visible = (scheduled || []).filter((c) => !cancelled.includes(c.id));

  // api.post avale le corps d'erreur (null) : on passe par api.fetch pour
  // remonter le message du serveur (date passée, commande déjà prise…).
  const call = async (path, body) => {
    const r = await api.fetch(path, { method: 'POST', body: JSON.stringify(body) });
    const t = await r.text();
    let json = null;
    try {
      json = t ? JSON.parse(t) : null;
    } catch {
      /* corps non JSON */
    }
    return { ok: r.ok, json };
  };

  const submit = async () => {
    if (whenInvalid || !confirmOk) return;
    setSaving(true);
    setError(null);
    const body = {
      // Le champ est en heure LOCALE, le serveur raisonne en UTC.
      scheduledAt: mode === 'schedule' ? new Date(whenLocal).toISOString() : null,
      reason: reason.trim() || null,
      maintenanceMinutes: maintenance ? 30 : 0,
    };
    const { ok, json } = await call(`/platform/devices/${agentId}/restart`, body);
    setSaving(false);
    if (ok) onClose(true);
    else setError(json?.error || 'Échec de la demande de redémarrage.');
  };

  const cancel = async (id) => {
    setError(null);
    const { ok, json } = await call(`/platform/commands/${id}/cancel`, {});
    if (ok) {
      setCancelled((l) => [...l, id]);
      setChanged(true);
    } else setError(json?.error || "Échec de l'annulation.");
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
      <div className="bg-card border border-rule rounded-2xl w-[480px] max-w-full max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 pt-6 pb-4 flex-none">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Power size={18} strokeWidth={1.75} className="text-blue-400" />
              <h2 className="text-base font-bold text-fg">Redémarrer l'appareil</h2>
            </div>
            <button
              onClick={() => onClose(changed)}
              className="bg-transparent border-none text-muted cursor-pointer hover:text-fg p-1"
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>
          <div className="text-[12px] text-muted-2">
            Appareil : <span className="text-fg-2 font-semibold">{label}</span>
          </div>
        </div>

        <div className="px-6 pb-6 overflow-y-auto flex-1 min-h-0">
          {isServer && (
            <div className="bg-warn/10 border border-warn/30 rounded-xl px-3.5 py-2.5 mb-4 text-[12px] text-fg-2 flex gap-2">
              <AlertTriangle size={14} strokeWidth={1.75} className="text-warn flex-shrink-0 mt-0.5" />
              <span>
                Cet appareil est un serveur : le redémarrage interrompra les services qu'il héberge.
              </span>
            </div>
          )}

          {canRun && (
            <>
              <div className="flex flex-col gap-2 mb-3 text-[13px] text-fg-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={mode === 'schedule'} onChange={() => setMode('schedule')} />
                  Planifier
                </label>
                {mode === 'schedule' && (
                  <div className="pl-6">
                    <input
                      type="datetime-local"
                      value={whenLocal}
                      onChange={(e) => setWhenLocal(e.target.value)}
                      className={`bg-sidebar border rounded-lg px-2.5 py-1.5 text-fg-2 text-[12px] outline-none ${
                        whenInvalid ? 'border-crit' : 'border-rule'
                      }`}
                    />
                    <div className="text-[10.5px] text-muted-2 mt-1">
                      {whenInvalid ? 'Date passée — choisir un moment à venir.' : 'Heure locale.'}
                    </div>
                  </div>
                )}
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={mode === 'now'} onChange={() => setMode('now')} />
                  Maintenant
                </label>
              </div>

              {mode === 'now' && (
                <div className="bg-crit/10 border border-crit/30 rounded-xl px-3.5 py-2.5 mb-3 text-[12px] text-fg-2">
                  La machine d'un client va redémarrer immédiatement. Pour confirmer, taper{' '}
                  <span className="font-mono font-semibold">{CONFIRM_PHRASE}</span> :
                  <input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className="mt-1.5 w-full bg-sidebar border border-rule rounded-lg px-2.5 py-1.5 text-fg-2 text-[12px] outline-none"
                    autoComplete="off"
                  />
                </div>
              )}

              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={300}
                placeholder="Motif (facultatif)"
                className="w-full bg-sidebar border border-rule rounded-lg px-2.5 py-1.5 text-fg-2 text-[12px] outline-none mb-3"
              />

              <label className="inline-flex items-center gap-2 cursor-pointer text-[12px] text-muted hover:text-fg-2 mb-4">
                <input type="checkbox" checked={maintenance} onChange={(e) => setMaintenance(e.target.checked)} />
                Mettre l'appareil en maintenance pendant 30 min
              </label>

              <button
                onClick={submit}
                disabled={saving || whenInvalid || !confirmOk}
                className="w-full bg-blue-500 border-none rounded-lg px-3.5 py-2 text-white text-xs font-semibold cursor-pointer hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Envoi…' : mode === 'now' ? 'Redémarrer maintenant' : 'Planifier le redémarrage'}
              </button>
            </>
          )}

          {error && (
            <div className="mt-3 px-3.5 py-2 rounded-lg text-xs font-semibold border bg-crit/12 border-crit/30 text-red-300">
              {error}
            </div>
          )}

          <div className="mt-5">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-2 mb-2 flex items-center gap-1.5">
              <Clock size={12} strokeWidth={1.75} /> Commandes planifiées
            </div>
            {visible.length === 0 ? (
              <div className="text-[12px] text-muted-2">Aucune commande planifiée.</div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {visible.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between gap-3 bg-sidebar border border-rule rounded-lg px-3 py-2"
                  >
                    <div className="text-[12px] text-fg-2 min-w-0">
                      <div className="font-semibold">
                        {scheduledLabel(c)} — {fmtWhen(c.scheduledAt)}
                      </div>
                      <div className="text-[10.5px] text-muted-2 truncate">
                        {c.requestedBy}
                        {c.reason ? ' · ' + c.reason : ''}
                      </div>
                    </div>
                    {canRun && (
                      <button
                        onClick={() => cancel(c.id)}
                        className="bg-transparent border border-crit/40 rounded-lg px-2.5 py-1 text-red-300 cursor-pointer text-[11px] font-semibold hover:bg-crit/15 flex-shrink-0"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
